import { Injectable } from '@angular/core';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { CollectionServiceBase } from '@portal-core/data/collection/services/collection.service.base';
import { GetDataOptions } from '@portal-core/data/common/models/get-data-options.model';
import { DataService } from '@portal-core/data/common/services/data.service';
import { ProjectChecklistItemStatus } from '@portal-core/project-checklists/enums/project-checklist-item-status.enum';
import { ProjectChecklistAnalyticsFileCount } from '@portal-core/project-checklists/models/project-checklist-analytics-file-count.model';
import { ProjectChecklistBuildAnalyticsOptions } from '@portal-core/project-checklists/models/project-checklist-build-analytics-options.model';
import { ProjectChecklist } from '@portal-core/project-checklists/models/project-checklist.model';
import { ProjectChecklistsApiService } from '@portal-core/project-checklists/services/project-checklists-api.service';
import { ProjectChecklistsDataService } from '@portal-core/project-checklists/services/project-checklists/project-checklists-data.service';
import { Resettable } from '@portal-core/util/resettable.decorator';
import { find, forEach } from 'lodash';
import { Observable, Subject, map, of, switchMap, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
@Resettable()
export class ProjectChecklistsService extends CollectionServiceBase<ProjectChecklist> {
  private checklistCreatedSource: Subject<number> = new Subject();
  public checklistCreated$: Observable<number> = this.checklistCreatedSource.asObservable();
  private checklistDeletedSource: Subject<number> = new Subject();
  public checklistDeleted$: Observable<number> = this.checklistDeletedSource.asObservable();

  constructor(
    private projectChecklistsDataService: ProjectChecklistsDataService,
    private projectChecklistsApiService: ProjectChecklistsApiService,
    protected dataService: DataService
  ) {
    super(projectChecklistsDataService, dataService);
  }

  protected fetchItemById$(checklistId: number): Observable<ProjectChecklist> {
    return this.projectChecklistsApiService.getChecklistById$(checklistId).pipe(map((checklist) => {
      delete checklist.Analytics;
      return checklist;
    }));
  }

  emitChecklistCreated(checklistId: number) {
    this.checklistCreatedSource.next(checklistId);
  }

  emitChecklistDeleted(checklistId: number) {
    this.checklistDeletedSource.next(checklistId);
  }

  getChecklistsPage$(projectId: number, filter?: PageFilter): Observable<Page<ProjectChecklist>> {
    return this.projectChecklistsApiService.getChecklistsPage$(projectId, filter);
  }

  getChecklistsNameAndIdByProjectId$(projectId: number, options?: GetDataOptions): Observable<ProjectChecklist[]> {
    return this.dataService.getDataListItems$<ProjectChecklist>('Projects', projectId, this.projectChecklistsDataService, {
      fetch: () => this.projectChecklistsApiService.getChecklistsNameAndIdByProjectId$(projectId).pipe(
        map(checklists => this.buildChecklistsAnalytics(checklists)),
        map(checklists => checklists.sort((checklistA, checklistB) => checklistA.Name.localeCompare(checklistB.Name)))
      )
    }, options);
  }

  createProjectChecklist$(checklist: ProjectChecklist): Observable<ProjectChecklist> {
    return this.projectChecklistsApiService.upsertProjectChecklist$(checklist).pipe(
      switchMap(newChecklist => {
        return this.projectChecklistsDataService.addItems$([this.buildChecklistAnalytics(newChecklist)]).pipe(
          map(() => newChecklist)
        );
      }),
      tap(newChecklist => this.emitChecklistCreated(newChecklist.Id))
    );
  }

  updateProjectChecklist$(checklist: ProjectChecklist): Observable<any> {
    return this.projectChecklistsApiService.upsertProjectChecklist$(checklist).pipe(
      tap(updateChecklist => {
        updateChecklist = this.buildChecklistAnalytics(updateChecklist);
        this.projectChecklistsDataService.updateItems$([updateChecklist]);
      })
    );
  }

  deleteProjectChecklist$(checklistId: number, projectId: number): Observable<any> {
    return this.projectChecklistsApiService.deleteProjectChecklist$(checklistId).pipe(
      switchMap(() => this.projectChecklistsDataService.updateItems$([{ Id: checklistId, IsDeleted: true }])),
      tap(() => this.emitChecklistDeleted(checklistId))
    );
  }

  buildChecklistsAnalytics(checklists: ProjectChecklist[], options?: ProjectChecklistBuildAnalyticsOptions): ProjectChecklist[] {
    return checklists.map(checklist => this.buildChecklistAnalytics(checklist, options));
  }

  buildChecklistAnalytics(checklist: ProjectChecklist, options: ProjectChecklistBuildAnalyticsOptions = {}) {
    if (!checklist.Analytics) {
      return checklist;
    }
    // Add up the number of newly invalid status and the number of newly completed statuses
    let invalidStatusCount = 0,
      completedStatusCount = 0;

    if (Array.isArray(options.changedStatuses)) {
      options.changedStatuses.forEach(status => {
        invalidStatusCount -= (status.oldValue === ProjectChecklistItemStatus.NA ? 1 : 0);
        invalidStatusCount += (status.newValue === ProjectChecklistItemStatus.NA ? 1 : 0);

        completedStatusCount -= (status.oldValue === ProjectChecklistItemStatus.Completed ? 1 : 0);
        completedStatusCount += (status.newValue === ProjectChecklistItemStatus.Completed ? 1 : 0);
      });
    }

    checklist.Analytics.TotalStatusCount -= invalidStatusCount;

    // Update the total status file counts
    if (Array.isArray(checklist.Analytics.TotalStatusFileCounts)) {
      checklist.Analytics.TotalStatusFileCounts.forEach(statusInfo => {
        let statusCount = 0;

        // Go through each changed status in order to update the count
        if (Array.isArray(options.changedStatuses)) {
          options.changedStatuses.forEach(status => {
            // Update the count for this status based on the old and new value
            statusCount -= (status.oldValue === statusInfo.Status ? 1 : 0);
            statusCount += (status.newValue === statusInfo.Status ? 1 : 0);
          });
        }

        // Update the status with the new count
        statusInfo.Count += statusCount;
        if (checklist.Analytics.TotalStatusCount <= 0) {
          if (statusInfo.Legend === 'Completed') {
            statusInfo.Percent = 100;
          } else {
            statusInfo.Percent = 0;
          }
        } else {
          statusInfo.Percent = (statusInfo.Count / checklist.Analytics.TotalStatusCount) * 100;
        }
      });
    }
    // Update the column status file counts
    forEach(checklist.Analytics.ColumnFileCounts, (columnStatuses: ProjectChecklistAnalyticsFileCount[], columnId: string) => {
      const columnCompletedStatusInfo = find(columnStatuses, statusInfo => statusInfo.Status === ProjectChecklistItemStatus.Completed);
      if (columnCompletedStatusInfo) {
        // If changes were made to this column
        if (parseInt(columnId, 10) === options.changedColumnId) { // == on purpose because columnId is a string but options.columnId is a number
          columnCompletedStatusInfo.Count += completedStatusCount;
          columnCompletedStatusInfo.Total -= invalidStatusCount;
        }

        columnCompletedStatusInfo.Percent = columnCompletedStatusInfo.Total <= 0 ? 100 : (columnCompletedStatusInfo.Count / columnCompletedStatusInfo.Total) * 100;
      }
    });


    // Update the completed percentage
    let completedStatusInfo;

    if (Array.isArray(checklist.Analytics.TotalStatusFileCounts)) {
      completedStatusInfo = checklist.Analytics.TotalStatusFileCounts.find(statusInfo => statusInfo.Status === ProjectChecklistItemStatus.Completed);
    }

    if (checklist.Analytics.TotalStatusCount > 0) {
      checklist.Analytics.CompletedPercent = ((completedStatusInfo ? completedStatusInfo.Count : 0) / checklist.Analytics.TotalStatusCount) * 100;
    } else {
      checklist.Analytics.CompletedPercent = 0;
    }
    // Return the checklist
    return checklist;
  }

  loadChecklistAnalytics$(checklistIds: number[]): Observable<any> {
    return this.projectChecklistsApiService.getChecklistAnalyticsByIds$(checklistIds).pipe(
      switchMap(analytics => {
        const updatedChecklists: ProjectChecklist[] = [];

        Object.entries(analytics).forEach(([checklistId, analytics]) => {
          const checklist = this.projectChecklistsDataService.getItemById(checklistId);
          if (checklist) {
            checklist.Analytics = analytics;
            updatedChecklists.push(this.buildChecklistAnalytics(checklist));
          }
        });

        if (updatedChecklists.length > 0) {
          return this.projectChecklistsDataService.updateItems$(updatedChecklists);
        } else {
          return of(null);
        }
      })
    );
  }

  verifyChecklistName$(projectId: number, checklistName: string): Observable<any> {
    return this.projectChecklistsApiService.verifyChecklistName$(projectId, checklistName);
  }
}
